import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';

/**
 * Modifiche degli altri, mentre hai l'app aperta.
 *
 * Due comportamenti, e uno dipende dall'altro:
 *
 *   1. **si aggiorna da solo.** Quando qualcuno del gruppo cambia qualcosa, le
 *      schermate aperte si rileggono. Senza, due persone finiscono a discutere
 *      di due itinerari diversi senza sapere perché;
 *   2. **dice cosa è cambiato e per mano di chi.** Un'app che si riscrive sotto
 *      gli occhi senza spiegare inquieta. «Luca ha aggiunto una tappa» no.
 *
 * Il canale è SSE: una connessione sola, che il browser riapre da sé se la rete
 * cade — cosa che in viaggio succede di continuo. Quando la scheda torna in
 * primo piano si rileggono comunque i dati, perché sul telefono le connessioni
 * in sottofondo vengono chiuse dal sistema senza avvisare nessuno.
 *
 * Limite dichiarato: arriva a chi ha **l'app aperta**. Con il telefono in tasca
 * servirebbero le notifiche push di sistema, che questa installazione non ha.
 */

export interface ModificaViaggio {
  readonly id: string;
  readonly actorName: string;
  readonly summary: string;
  readonly entity: string;
  readonly createdAt: string;
}

export function useTripEvents(tripId: string | null): ModificaViaggio | null {
  const queryClient = useQueryClient();
  const [ultima, setUltima] = useState<ModificaViaggio | null>(null);

  useEffect(() => {
    if (tripId === null) return undefined;

    const aggiorna = (): void => {
      // Si invalidano le letture del viaggio, non tutto: le foto dei luoghi e
      // le capacità del server non cambiano per una tappa spostata.
      for (const chiave of ['today', 'day', 'trip', 'trips', 'wishlist', 'budget', 'flights', 'accommodations', 'proposals', 'checklist', 'activity']) {
        void queryClient.invalidateQueries({ queryKey: [chiave] });
      }
    };

    const sorgente = new EventSource(`/api/trips/${tripId}/events`, { withCredentials: true });

    sorgente.onmessage = (evento: MessageEvent<string>) => {
      try {
        const voce = JSON.parse(evento.data) as ModificaViaggio;
        setUltima(voce);
        aggiorna();
      } catch {
        // Messaggio malformato: si rilegge comunque, che è la parte utile.
        aggiorna();
      }
    };

    // Il browser riapre da solo dopo `retry`. Non serve fare nulla qui se non
    // evitare che un errore ripetuto riempia la console.
    sorgente.onerror = () => undefined;

    // Ritorno in primo piano: sul telefono la connessione viene chiusa dal
    // sistema mentre l'app è in sottofondo, spesso senza che nessuno lo dica.
    const alRitorno = (): void => {
      if (document.visibilityState === 'visible') aggiorna();
    };
    document.addEventListener('visibilitychange', alRitorno);

    return () => {
      sorgente.close();
      document.removeEventListener('visibilitychange', alRitorno);
    };
  }, [tripId, queryClient]);

  return ultima;
}

/**
 * Avviso di modifica altrui.
 *
 * Compare in alto per pochi secondi e se ne va: non è un messaggio da leggere
 * e archiviare, è un «guarda che è cambiato qualcosa». La cronologia completa
 * sta in *Viaggio*.
 */
export function TripEventToast({ modifica }: { modifica: ModificaViaggio | null }): ReactNode {
  const [visibile, setVisibile] = useState(false);

  useEffect(() => {
    if (modifica === null) return undefined;
    setVisibile(true);
    const timer = setTimeout(() => {
      setVisibile(false);
    }, 6000);
    return () => {
      clearTimeout(timer);
    };
  }, [modifica]);

  if (modifica === null || !visibile) return null;

  return (
    <div
      role="status"
      className="safe-top fixed inset-x-0 top-0 z-50 mx-auto flex max-w-[var(--content-max-width)] items-center gap-2 px-4 pt-2"
    >
      <div className="flex w-full items-center gap-2 rounded-[var(--r-md)] bg-brand-soft px-3 py-2 shadow-[var(--shadow-raised)]">
        <Users size={16} className="shrink-0 text-brand-strong" aria-hidden="true" />
        <p className="text-[14px] leading-5 text-brand-strong">
          <strong>{modifica.actorName}</strong> {modifica.summary}
        </p>
      </div>
    </div>
  );
}
